import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowLeft, Check, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { DEMO_PROFILES, pick } from "../lib/industries";
import { journeyT } from "../lib/journey";
import { Logo } from "../components/Logo";

const TXT = {
  fr: { q: "Quel type d'entreprise souhaitez-vous découvrir ?", back: "Retour", demoTag: "DONNÉES DE DÉMONSTRATION" },
  en: { q: "Which type of business do you want to explore?", back: "Back", demoTag: "DEMO DATA" },
  es: { q: "¿Qué tipo de empresa quieres descubrir?", back: "Volver", demoTag: "DATOS DE DEMOSTRACIÓN" },
};

export default function DemoPicker() {
  const nav = useNavigate();
  const { startDemo } = useApp();
  const { lang } = useLang();
  const L = TXT[lang] || TXT.fr;
  const J = journeyT(lang);
  const [phase, setPhase] = useState("pick");
  const [step, setStep] = useState(-1);
  const [done, setDone] = useState(false);
  const [opps, setOpps] = useState(6);
  const timers = useRef([]);

  const choose = (id) => {
    const p = DEMO_PROFILES.find((x) => x.id === id);
    setOpps(p?.opportunities || 6);
    setPhase("wow"); setStep(-1); setDone(false);
    const items = J.analyzeItems;
    // Reveal the 6 analyzed metrics quickly (~0.35s each), then confirm.
    items.forEach((_, i) => {
      timers.current.push(setTimeout(() => setStep(i), 300 + i * 350));
    });
    timers.current.push(setTimeout(() => setDone(true), 300 + items.length * 350 + 250));
    timers.current.push(setTimeout(() => { startDemo(id); nav("/demo/story"); }, 300 + items.length * 350 + 1400));
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden grain flex flex-col">
      <div className="fixed inset-0 pointer-events-none z-0"><div className="absolute inset-0 aurora" /><div className="absolute inset-0 tech-grid opacity-60" /></div>
      <header className="z-30 glass border-b border-white/5"><div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
        <Logo />
        {phase === "pick" && <button data-testid="demo-back" onClick={() => nav("/")} className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white flex items-center gap-1"><ArrowLeft size={16} /> {L.back}</button>}
      </div></header>

      <main className="relative z-10 flex-1 max-w-5xl mx-auto w-full px-4 py-10 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {phase === "pick" ? (
            <motion.div key="pick" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <p className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-neon/15 text-neon mb-3">{L.demoTag}</p>
              {/* Jury hook: what they'll understand in 2 minutes */}
              <div className="lb-card p-4 mb-5 border border-neon/20 bg-neon/[0.04] flex items-start gap-3" data-testid="demo-tagline">
                <div className="w-8 h-8 rounded-lg bg-neon/15 text-neon flex items-center justify-center shrink-0 mt-0.5"><Sparkles size={16} /></div>
                <p className="text-sm sm:text-base text-white/90 leading-relaxed">{J.tagline}</p>
              </div>
              <h1 className="font-display font-black text-2xl sm:text-4xl mb-2">{L.q}</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{J.startHint}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {DEMO_PROFILES.map((p) => (
                  <motion.button key={p.id} data-testid={`demo-profile-${p.id}`} onClick={() => choose(p.id)}
                    whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}
                    className="lb-card p-5 text-left hover:border-neon/40 border border-white/10 transition-colors">
                    <div className="text-3xl mb-2">{p.emoji}</div>
                    <p className="font-semibold text-sm">{pick(p.label, lang)}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 truncate">{pick(p.sector, lang)}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="wow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md mx-auto w-full text-center py-6">
              <motion.div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center mb-6"
                animate={done ? { scale: [1, 1.12, 1] } : {}} transition={{ duration: 0.5 }}>
                {done ? <Check size={30} className="text-white" /> : <Loader2 size={30} className="animate-spin text-white" />}
              </motion.div>
              <h2 className="font-display font-bold text-lg sm:text-xl mb-6" data-testid="analyze-heading">{J.analyzing}</h2>
              <div className="space-y-2.5 text-left">
                {J.analyzeItems.map((s, i) => (
                  <AnimatePresence key={i}>
                    {i <= step && (
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 text-sm" data-testid={`analyze-item-${i}`}>
                        <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><Check size={13} className="text-emerald-400" /></span>
                        <span className="text-white/90">{s}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                ))}
              </div>
              <AnimatePresence>
                {done && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-7" data-testid="analyze-done">
                    <p className="font-display font-black text-neon tracking-wide">{J.analyzeDone}</p>
                    <p className="text-sm text-white/90 mt-1">{J.oppsDetected(opps)}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
